import ControlCanvaSXBehaviors from '../controls/ControlCanvasXBehaviors';
import { fabric } from 'fabric';

// Generic control base that adds some utility methods.
class ControlBase extends fabric.Control {
  // Whether the mouse is currently over this control. Can be used to
  // render a hover state for the control.
  hovered: boolean = false;

  // If the scaled width or height of the object is below this value then the
  // control will be hidden. Set to 0 to always show the control.
  minDimension: number = 14;

  // Stores information used by this control that varies depending on
  // which object the control is tied to. This helps use a single
  // instance of the control object for all objects on the whiteboard.
  //
  // Stored in a WeakMap indexed by object so it can be garbage
  // collected when the object is out of scope.
  objectState = new WeakMap<
    fabric.Object,
    ReturnType<typeof this.initialObjectState>
  >();

  constructor(options) {
    super(options);

    this.mouseDownHandler = this.mouseDownHandler.bind(this);
    this.mouseUpHandler = this.mouseUpHandler.bind(this);
  }

  initialObjectState() {
    return {};
  }

  getState(
    fabricObject: fabric.Object
  ): ReturnType<typeof this.initialObjectState> {
    let state = this.objectState.get(fabricObject);

    if (!state) {
      state = this.initialObjectState();
      this.objectState.set(fabricObject, state);
    }

    return state;
  }

  getActionHandler(
    eventData: MouseEvent,
    fabricObject: fabric.Object,
    control: fabric.Control
  ): fabric.ControlMouseEventHandler {
    return (
      eventData: MouseEvent,
      transform: fabric.Transform,
      x: number,
      y: number
    ) => {
      // Custom event to give objects some insight into the action being performed
      // on them. This allows for custom behavior based on the action. This behavior
      // should not conflict with the default behavior of the control.
      fabricObject.fire('before:control:action', {
        control,
        ...eventData,
      });
      return control.actionHandler.apply(this, [eventData, transform, x, y]);
    };
  }

  mouseDownHandler(
    eventData: MouseEvent,
    transform: fabric.Transform,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    x: number,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    y: number
  ): boolean {
    const { target } = transform;
    target.canvas.setCursor(this.cursorStyleHandler(eventData, this, target));
    target.canvas.requestRenderAll();

    return false;
  }

  mouseUpHandler(
    eventData: MouseEvent,
    transform: fabric.Transform,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    x: number,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    y: number
  ): boolean {
    const target = transform?.target;
    if (target) {
      target.canvas.setCursor(this.cursorStyleHandler(eventData, this, target));
      target.canvas.requestRenderAll();
    }

    return false;
  }

  onMouseOver(eventData, fabricObject) {
    this.hovered = true;
    fabricObject.canvas.requestRenderAll();
  }

  onMouseOut(eventData, fabricObject) {
    this.hovered = false;
    fabricObject.canvas.requestRenderAll();
  }

  onMouseMove(eventData, fabricObject) {
    this.hovered = true;
    fabricObject.canvas.requestRenderAll();
  }

  onMouseWheel(eventData, fabricObject) {
    fabricObject.canvas.requestRenderAll();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  dblClickHandler(eventData, fabricObject) {}

  getVisibility(fabricObject, controlKey): boolean {
    if (!super.getVisibility(fabricObject, controlKey)) return false;

    const canvasZoom = fabricObject.canvas?.getZoom() || 1;
    return (
      Math.min(
        fabricObject.width * fabricObject.scaleX,
        fabricObject.height * fabricObject.scaleY
      ) >=
      this.minDimension / canvasZoom
    );
  }

  // Whether the control is currently being dragged. Useful to render an
  // active state for the control.
  isActive(fabricObject: fabric.Object): boolean {
    const transform = fabricObject.canvas?._currentTransform;
    if (!transform) return false;
    if (transform.target !== fabricObject) return false;
    if (!transform.corner) return false;
    if (fabricObject.controls[transform.corner] !== this) return false;

    return true;
  }

  isHovered() {
    return this.hovered;
  }

  // Returns whether a point should be considered a part of this
  // control. Used for per-pixel handling of non-rectangular controls
  // or controls that handle their own positioning.
  containsPoint?(
    _point: { x: number; y: number },
    _fabricObject: fabric.Object
  ): boolean;
}

export { ControlBase as PlainControlBase };

export default class DecoratedControlBase extends ControlCanvaSXBehaviors(
  ControlBase
) {}
